import React, { useRef } from 'react';
import { FaLinkedin, FaGithub, FaBars, FaTimes, FaSun, FaMoon } from 'react-icons/fa';
import { LuDownload } from 'react-icons/lu';
import mobileStyles from '../../styles/navigation/MobileNavbar.module.css';
import utilities from '../../styles/utilities.module.css';
import { getResumeUrl } from '../../utils/resumeUtils';
import { useTheme } from '../../hooks/useTheme';
import { useGlobalLayerState } from '../../hooks/useLayerState';
import { githubUrl, linkedinUrl } from '../../data/personal';
import { wrapIcon } from '../../utils/iconWrapper';
import { NavBarProps } from './types';

const LinkedInIcon = wrapIcon(FaLinkedin);
const GitHubIcon = wrapIcon(FaGithub);
const BarsIcon = wrapIcon(FaBars);
const TimesIcon = wrapIcon(FaTimes);
const ResumeIcon = wrapIcon(LuDownload);
const SunIcon = wrapIcon(FaSun);
const MoonIcon = wrapIcon(FaMoon);

const MobileNavbar: React.FC<NavBarProps> = ({ currentSection, onSectionChange }) => {
    // Theme management
    const { toggleTheme, isLight } = useTheme();

    // Layer state management for mobile menu
    const { showMobileMenu, showDefault, getCurrentPreset } = useGlobalLayerState();
    const isMenuOpen = getCurrentPreset() === 'MOBILE_MENU_ACTIVE';

    // Update the current section when the element is clicked
    const navRef = useRef<HTMLElement>(null);
    const handleNavClick = (section: string, e: React.MouseEvent) => {
        e.preventDefault();
        const sectionElement = document.getElementById(section);
        if (sectionElement) {
            sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        onSectionChange(section);
        
        // Close the menu after navigation
        showDefault();
        document.documentElement.style.setProperty('--navbar-lower-menu-display', `none`);
    };

    // Toggle the menu open/close state
    const toggleMenu = () => {
        if (isMenuOpen) {
            // Menu is open, so close it
            showDefault();
            document.documentElement.style.setProperty('--navbar-lower-menu-display', `none`);
        } else {
            // Menu is closed, so open it
            showMobileMenu();
            document.documentElement.style.setProperty('--navbar-lower-menu-display', `flex`);
        }
    };

    return (
        <nav className={`${mobileStyles.navbar} ${isMenuOpen ? mobileStyles.menuOpen : ''}`} ref={navRef}>
            <button className={`${mobileStyles.menuButton} ${utilities.interactivePadding}`} onClick={toggleMenu}>
                {isMenuOpen ? <TimesIcon /> : <BarsIcon />}
            </button>
            <div className={`${mobileStyles.pageName} ${utilities.interactivePadding}`}>{currentSection}</div>
            <ul className={`${mobileStyles.mobileNavbarItemHolder} ${isMenuOpen ? mobileStyles.showMenu : ''}`}>
                <li>
                    <a
                        onClick={(e) => handleNavClick('home', e)}
                        href="#home"
                        className={currentSection === 'home' ? mobileStyles.active : ''}
                    >
                        Home
                    </a>
                </li>
                <li>
                    <a
                        onClick={(e) => handleNavClick('projects', e)}
                        href="#projects"
                        className={currentSection === 'projects' ? mobileStyles.active : ''}
                    >
                        Projects
                    </a>
                </li>
                <li>
                    <a
                        onClick={(e) => handleNavClick('career', e)}
                        href="#career"
                        className={currentSection === 'career' ? mobileStyles.active : ''}
                    >
                        Career
                    </a>
                </li>
                <li className={mobileStyles.separator}>&nbsp;</li>
                <li>
                    <a
                        href={linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <LinkedInIcon /> LinkedIn
                    </a>
                </li>
                <li>
                    <a
                        href={githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <GitHubIcon /> GitHub
                    </a>
                </li>
                <li>
                    <a
                        href={getResumeUrl()}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <ResumeIcon /> Resume
                    </a>
                </li>
                <li className={mobileStyles.separator}>&nbsp;</li>
                <li>
                    <button
                        onClick={toggleTheme}
                        className={mobileStyles.themeToggle}
                        aria-label={`Switch to ${isLight ? 'dark' : 'light'} mode`}
                    >
                        {isLight ? <MoonIcon /> : <SunIcon />} 
                        {isLight ? 'Dark' : 'Light'}
                    </button>
                </li>
            </ul>
        </nav>
    );
}

export default MobileNavbar;